import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import type { MouseEvent, ReactElement, ReactNode } from 'react';
import React from 'react';

/**
 * Primary border + icon color when selection-ring chrome is selected.
 * Scoped under `:not(.Mui-disabled)` so a disabled trigger falls back to MUI's own
 * disabled palette instead of staying primary-colored.
 */
export const Q_SELECT_SELECTION_RING_SX = {
  '&:not(.Mui-disabled)': {
    borderColor: 'primary.main',
    color: 'primary.main',
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: 'inherit',
    },
  },
} as const satisfies SxProps<Theme>;

const selectionRingBadgeSx = (theme: Theme): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'top',
  '& .MuiBadge-badge': {
    height: 8,
    minWidth: 8,
    top: 0,
    right: 3,
    transform: 'scale(1) translate(50%, 0)',
    transformOrigin: '100% 0%',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  '& .MuiBadge-badge.MuiBadge-invisible': {
    transform: 'scale(0) translate(50%, 0)',
  },
});

type QSelectSelectionRingProps = {
  /** When true, shows the primary corner badge dot. */
  selected: boolean;
  children: ReactElement;
};

/**
 * Corner badge-dot wrapper for selection-ring chrome.
 * Use with {@link QSelectButton} `selectionRing`, or around a compact IconButton trigger.
 */
export function QSelectSelectionRing({ selected, children }: QSelectSelectionRingProps): ReactElement {
  const theme = useTheme();
  return (
    <Badge
      variant='dot'
      color='primary'
      invisible={!selected}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={selectionRingBadgeSx(theme)}
    >
      {children}
    </Badge>
  );
}

export type QSelectButtonProps = Omit<ButtonProps, 'endIcon' | 'variant'> & {
  /** When true, shows the up caret and `action.hover` background (menu open). */
  open?: boolean;
  /** Defaults to `"outlined"`. */
  variant?: ButtonProps['variant'];
  /** When true, omits the dropdown caret. Defaults to false. */
  hideEndIcon?: boolean;
  /**
   * Opt-in selection-ring chrome (primary border + corner badge dot).
   * Ring and dot only render when this is true **and** {@link selected} is true.
   */
  selectionRing?: boolean;
  /**
   * Whether a value is selected. Turns on selection-ring chrome when
   * {@link selectionRing} is true, and the clear end-affordance when {@link onClear} is set.
   */
  selected?: boolean;
  /**
   * When set and {@link selected} is true, replaces the dropdown caret with a
   * separately focusable clear control. Clicking it does not open the menu.
   */
  onClear?: (event: MouseEvent<HTMLElement>) => void;
  /** Accessible name for the clear control. Defaults to `"Clear"`. */
  clearAriaLabel?: string;
};

const caretEndIcon = (open: boolean): ReactNode => (open ? <ArrowDropUpRoundedIcon /> : <ArrowDropDownRoundedIcon />);

/**
 * Outlined trigger for {@link QSelect}.
 * Forwards `size` and other MUI Button props; owns caret + default outlined look.
 * Forwards `ref` to the underlying Button so QSelect can anchor its menu off the
 * trigger element even when opened without a click (parent-controlled `open`).
 */
export function QSelectButton({
  open = false,
  variant = 'outlined',
  hideEndIcon = false,
  selectionRing = false,
  selected = false,
  onClear,
  clearAriaLabel = 'Clear',
  children,
  sx,
  ref,
  ...buttonProps
}: QSelectButtonProps): ReactElement {
  const showSelectionRing = selectionRing && selected;
  const showClear = Boolean(onClear) && selected && !hideEndIcon;

  const handleClear = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    onClear?.(event);
  };

  const button = (
    <Button
      ref={ref}
      variant={variant}
      endIcon={
        hideEndIcon ? undefined : showClear ? <Box aria-hidden sx={{ width: 20, height: 20 }} /> : caretEndIcon(open)
      }
      aria-haspopup='menu'
      aria-expanded={open ? 'true' : undefined}
      {...buttonProps}
      sharedcomponentid='QSelectButton'
      sx={[
        {
          px: 1,
          '& .MuiButton-startIcon': { marginLeft: 0 },
          '&[aria-expanded="true"]': {
            backgroundColor: 'action.hover',
            '&:hover': { backgroundColor: 'action.hover' },
          },
        },
        ...(showSelectionRing ? [Q_SELECT_SELECTION_RING_SX] : []),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Button>
  );

  const control = showClear ? (
    <Box sx={{ display: 'inline-flex', position: 'relative' }}>
      {button}
      <IconButton
        aria-label={clearAriaLabel}
        disabled={buttonProps.disabled}
        onClick={handleClear}
        onMouseDown={(event) => event.stopPropagation()}
        size='small'
        sx={{
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 24,
          height: 24,
          p: 0,
          zIndex: 1,
          color: 'icon.main',
        }}
      >
        <CancelRoundedIcon fontSize='small' />
      </IconButton>
    </Box>
  ) : (
    button
  );

  if (!selectionRing) {
    return control;
  }

  return <QSelectSelectionRing selected={selected}>{control}</QSelectSelectionRing>;
}
